/**
 * Font bond accessor and DOM injection logic.
 *
 * Font bond packages (e.g. `@molecule/app-fonts-arimo`) export a
 * `FontDefinition`, which is passed to `setFont()` to register the font
 * and inject the necessary CSS into the document head.
 *
 * @module
 */
import { bond, get as bondGet, isBonded, unbondAll } from '@molecule/app-bond';
import { buildFontFamily, systemMono, systemSans, systemSerif } from './utilities.js';
const BOND_TYPE = 'font';
/**
 * Resolves the public base path local font files are served under, always
 * with a leading and a trailing slash (`/`, `/blog/`).
 *
 * Order: the explicit `basePath` → the document's `<base href>` → Vite's
 * `import.meta.env.BASE_URL` (the `base` option, present in Vite-built and
 * Vite-served code) → `/`. A site served from a sub-path (`base: '/blog/'`)
 * has its `public/fonts/` at `/blog/fonts/`, so a root-absolute `/fonts/…`
 * 404s there and the fallback stack renders instead.
 *
 * @param basePath - An explicit base, e.g. `import.meta.env.BASE_URL` or `/blog`.
 * @returns The normalized base path.
 */
export function resolveFontBasePath(basePath) {
    let base = basePath?.trim();
    if (!base && typeof document !== 'undefined') {
        const baseHref = document.querySelector('base[href]')?.getAttribute('href');
        if (baseHref) {
            try {
                base = new URL(baseHref, document.baseURI || 'http://localhost/').pathname;
            }
            catch (_error) {
                // Not a URL the parser accepts: use the attribute as the path.
                base = baseHref;
            }
        }
    }
    if (!base) {
        const env = import.meta.env;
        if (typeof env?.BASE_URL === 'string')
            base = env.BASE_URL;
    }
    if (!base || base === '.' || base === './')
        return '/';
    if (!base.startsWith('/'))
        base = `/${base}`;
    if (!base.endsWith('/'))
        base = `${base}/`;
    return base;
}
/** A `faces[].file` that already names its own final location. */
const ABSOLUTE_FACE_FILE = /^(?:[a-z][a-z0-9+.-]*:|\/\/|\/)/i;
/**
 * Whether a local face's `file` is already a final URL, so `setFont()` must use
 * it as-is instead of resolving it under `<basePath>fonts/`.
 *
 * True for a root-absolute path (`/assets/ionicons-1a2b.woff`), a full URL
 * (`https:`, `http:`, `//host/…`) or an inline `data:` URL. Those are exactly
 * the shapes a bundler emits for `import woff from '…woff?url'`.
 *
 * @param file - The face's `file` field.
 * @returns `true` when the value is already a URL.
 */
export function isAbsoluteFontFileUrl(file) {
    return ABSOLUTE_FACE_FILE.test(file);
}
/**
 * Resolves a local face's `file` to the URL its `@font-face` loads from.
 *
 * An already-final URL is returned unchanged (including a Vite asset URL that
 * already carries the configured `base`). A plain filename is served from
 * `<basePath>fonts/<file>`.
 *
 * @param file - The face's `file` field.
 * @param basePath - Public base path from {@link resolveFontBasePath} (default `/`).
 * @returns The URL to put in `src: url(…)`.
 */
export function resolveFontFaceUrl(file, basePath = '/') {
    if (isAbsoluteFontFileUrl(file))
        return file;
    const base = basePath.endsWith('/') ? basePath : `${basePath}/`;
    return `${base}fonts/${file}`;
}
/**
 * Registers a font definition for a specific role (`sans`, `serif`, or `mono`).
 *
 * Bonds the font via the named provider system, sets the
 * `--mol-font-{role}` CSS variable, and injects font loading
 * into the document head (CDN `<link>` tags or `@font-face` declarations).
 *
 * @param font - The font definition including family, role, and source configuration.
 * @param options - `basePath`: the public base the app is served under (pass
 *   `import.meta.env.BASE_URL` in a Vite app); local faces load from
 *   `<basePath>fonts/<file>`. See {@link resolveFontBasePath} for the default.
 */
export function setFont(font, options = {}) {
    bond(BOND_TYPE, font.role, font);
    if (typeof document !== 'undefined') {
        // Apply CSS variable
        document.documentElement.style.setProperty(`--mol-font-${font.role}`, buildFontFamily(font));
        if (font.source.type === 'system')
            return;
        // CDN fonts: inject <link> tags
        if (font.source.url) {
            const existing = document.querySelector(`link[href="${font.source.url}"]`);
            if (!existing) {
                for (const origin of font.source.preconnect ?? []) {
                    const pc = document.createElement('link');
                    pc.rel = 'preconnect';
                    pc.href = origin;
                    if (origin.includes('gstatic'))
                        pc.crossOrigin = '';
                    document.head.appendChild(pc);
                }
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = font.source.url;
                document.head.appendChild(link);
            }
        }
        // Local fonts: inject @font-face declarations
        if (font.source.type === 'local' && font.source.faces) {
            const styleId = `mol-font-${font.role}`;
            const existing = document.getElementById(styleId);
            if (existing)
                existing.remove();
            const base = resolveFontBasePath(options.basePath);
            const css = font.source.faces
                .map((face) => {
                const weight = face.weight;
                const style = face.style ?? 'normal';
                return `@font-face {
  font-family: '${font.family}';
  src: url('${resolveFontFaceUrl(face.file, base)}');
  font-weight: ${weight};
  font-style: ${style};
  font-display: swap;
}`;
            })
                .join('\n');
            // Icon fonts (e.g. Material Symbols) ship a utility class alongside the
            // @font-face — inject it here so the font is fully self-contained locally
            // (Google's CSS used to provide both; we no longer load it).
            const styleEl = document.createElement('style');
            styleEl.id = styleId;
            styleEl.textContent = font.utilityCss ? `${css}\n${font.utilityCss}` : css;
            document.head.appendChild(styleEl);
        }
    }
}
/**
 * Retrieves the font definition bonded for a specific role.
 *
 * @param role - The font role (`'sans'`, `'serif'`, or `'mono'`).
 * @returns The bonded font definition, or `undefined` if none is set.
 */
export function getFont(role) {
    return bondGet(BOND_TYPE, role);
}
/**
 * Checks whether a font has been bonded for a specific role.
 *
 * @param role - The font role to check.
 * @returns `true` if a font is bonded for the given role.
 */
export function hasFont(role) {
    return isBonded(BOND_TYPE, role);
}
/**
 * Returns the complete font configuration for all three roles.
 * Falls back to system font stacks for any role not explicitly set.
 *
 * @returns The resolved font config with `sans`, `serif`, and `mono` definitions.
 */
export function getFontConfig() {
    return {
        sans: bondGet(BOND_TYPE, 'sans') ?? systemSans,
        serif: bondGet(BOND_TYPE, 'serif') ?? systemSerif,
        mono: bondGet(BOND_TYPE, 'mono') ?? systemMono,
    };
}
/**
 * Checks whether any font role has been explicitly configured.
 *
 * @returns `true` if at least one font role is bonded.
 */
export function hasFontConfig() {
    return isBonded(BOND_TYPE, 'sans') || isBonded(BOND_TYPE, 'serif') || isBonded(BOND_TYPE, 'mono');
}
/**
 * Removes all bonded font definitions. Primarily used in tests.
 */
export function resetFonts() {
    unbondAll(BOND_TYPE);
}
//# sourceMappingURL=provider.js.map